import React, { useId } from "react";
import { Label } from "../../atoms/Label/Label";
import { Text } from "../../atoms/Text/Text";
import { cn } from "../../lib/cn";

export type FormFieldControlProps = {
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  state?: "default" | "error";
};

interface FormFieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactElement<FormFieldControlProps>;
}

// Label + a single control atom (Input/Textarea/...) + error/hint text —
// the control gets id/aria wiring injected so consumers never wire it by hand.
export function FormField({ label, htmlFor, error, hint, required, className = "", children }: FormFieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? children.props.id ?? generatedId;
  const messageId = `${id}-message`;
  const message = error ?? hint;

  const control = React.cloneElement(children, {
    id,
    "aria-invalid": Boolean(error) || undefined,
    "aria-describedby": message ? messageId : undefined,
    "aria-required": required || undefined,
    state: error ? "error" : children.props.state,
  });

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-error-fg" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </Label>
      {control}
      {message && (
        <Text as="p" id={messageId} size="xs" className={error ? "text-error-fg" : "text-text-subtle"}>
          {message}
        </Text>
      )}
    </div>
  );
}
